import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { 
  Play, Bell, BellOff, Users, Video, Calendar, 
  Instagram, ExternalLink, Share2, CheckCircle, Loader2,
  Clock, Eye, Heart, MessageCircle, Image as ImageIcon,
  ArrowLeft, MoreHorizontal, X, Home, LogIn, ChevronLeft, ChevronRight,
  BadgeCheck
} from 'lucide-react';
import SocialShareModal from '../components/SocialShareModal';
import VODFooter from '../components/VODFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format duration from seconds to mm:ss or hh:mm:ss
const formatDuration = (seconds) => {
  if (!seconds || isNaN(seconds) || seconds < 0) return '';
  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

// Check if on VOD domain
const isOnLtvvodDomain = () => {
  return window.location.hostname.includes('ltvvod');
};

// Get login URL with VOD context
const getLoginUrl = () => {
  const fromParam = isOnLtvvodDomain() ? 'from=ltvvod' : 'from=vod';
  const currentPath = window.location.pathname;
  
  // Store the creator page URL in sessionStorage as a backup
  sessionStorage.setItem('creatorPageReturn', currentPath);
  
  return `/live/login?${fromParam}&redirect=${encodeURIComponent(currentPath)}`;
};

// Social platform icons mapping
const SocialIcon = ({ platform, size = 'md' }) => {
  const sizeClass = size === 'sm' ? 'w-3.5 h-3.5' : 'w-5 h-5';
  switch (platform) {
    case 'instagram':
      return <Instagram className={sizeClass} />;
    case 'tiktok':
      return <svg className={sizeClass} viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>;
    case 'youtube':
      return <svg className={sizeClass} viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>;
    case 'twitter':
    case 'x':
      return <svg className={sizeClass} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
    case 'snapchat':
      return <svg className={sizeClass} viewBox="0 0 24 24" fill="currentColor"><path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301a.603.603 0 0 1 .274-.074c.12 0 .24.03.36.09.313.15.466.42.466.66a.853.853 0 0 1-.274.586c-.105.09-.225.165-.345.24-.18.12-.375.24-.54.3-.226.09-.406.165-.54.225-.12.06-.209.12-.269.18l-.003.005a.706.706 0 0 0-.093.156c.24.39.57.75.93 1.065.315.27.615.495.915.66a4.27 4.27 0 0 0 1.65.525c.195.015.39.045.57.09.256.06.434.18.539.33l.003.003a.655.655 0 0 1 .12.39c0 .255-.165.48-.465.66l-.015.015c-.21.12-.435.21-.66.285l-.015.006a8.12 8.12 0 0 1-2.325.495l-.005.001c-.015.21-.06.435-.165.645v.009c-.075.15-.15.255-.24.33l-.003.003c-.225.18-.576.195-1.005.195-.12 0-.24 0-.36-.015a5.19 5.19 0 0 1-.87-.12 4.473 4.473 0 0 0-1.23-.165c-.42 0-.855.075-1.29.24a4.69 4.69 0 0 1-.975.33c-.135.03-.285.06-.405.06-.585 0-.93-.27-1.125-.54-.195-.255-.27-.54-.27-.885 0-.045 0-.09.003-.135.015-.285-.045-.615-.12-.93a3.68 3.68 0 0 0-.21-.615 4.21 4.21 0 0 1-.135-.345l-.003-.015a8.12 8.12 0 0 1-2.325-.495l-.015-.006a2.52 2.52 0 0 1-.66-.285l-.015-.015c-.3-.18-.465-.405-.465-.66a.655.655 0 0 1 .12-.39l.003-.003c.105-.15.283-.27.539-.33.18-.045.375-.075.57-.09a4.27 4.27 0 0 0 1.65-.525c.3-.165.6-.39.915-.66.36-.315.69-.675.93-1.065a.706.706 0 0 0-.093-.156l-.003-.005c-.06-.06-.149-.12-.269-.18-.134-.06-.314-.135-.54-.225-.165-.06-.36-.18-.54-.3-.12-.075-.24-.15-.345-.24a.853.853 0 0 1-.274-.586c0-.24.153-.51.466-.66a.603.603 0 0 1 .634.016c.374.181.733.285 1.033.301.198 0 .326-.045.401-.09-.008-.165-.018-.33-.03-.51l-.003-.06c-.104-1.628-.23-3.654.299-4.847C7.859 1.069 11.216.793 12.206.793z"/></svg>;
    case 'facebook':
      return <svg className={sizeClass} viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;
    case 'website':
    case 'link':
      return <ExternalLink className={sizeClass} />;
    default:
      return <ExternalLink className={sizeClass} />;
  }
};

// Format view count
const formatViews = (views) => {
  if (!views) return '0 views';
  if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M views`;
  if (views >= 1000) return `${(views / 1000).toFixed(1)}K views`;
  return `${views} views`;
};

// Format relative time
const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
  if (seconds < 2592000) return `${Math.floor(seconds / 604800)} weeks ago`;
  if (seconds < 31536000) return `${Math.floor(seconds / 2592000)} months ago`;
  return `${Math.floor(seconds / 31536000)} years ago`;
};

const CreatorChannelPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [videos, setVideos] = useState([]);
  const [featuredVideo, setFeaturedVideo] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [activeTab, setActiveTab] = useState('videos');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSubscribeSuccess, setShowSubscribeSuccess] = useState(false);
  
  // Carousel scroll
  const videosContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Check if user is logged in
  const isLoggedIn = () => {
    const customer = localStorage.getItem('livestreamCustomer');
    return !!customer;
  };

  // Get logged in user's email
  const getLoggedInEmail = () => {
    try {
      const customer = JSON.parse(localStorage.getItem('livestreamCustomer') || '{}');
      return customer.email || localStorage.getItem('user_email');
    } catch {
      return localStorage.getItem('user_email');
    }
  };

  useEffect(() => {
    fetchCreatorData();
  }, [slug]);

  // Check if user is already subscribed when page loads or user logs in
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!creator || !isLoggedIn()) return;
      
      const email = getLoggedInEmail();
      if (!email) return;
      
      try {
        const response = await axios.get(`${API_URL}/api/creators/subscription-status`, {
          params: { email, creator_id: creator.id }
        });
        if (response.data.is_subscribed) {
          setIsSubscribed(true);
        }
      } catch (err) {
        console.log('Could not check subscription status:', err.message);
      }
    };
    
    checkSubscriptionStatus();
  }, [creator]);

  // Check for pending subscription after login redirect
  useEffect(() => {
    const pendingSubscription = sessionStorage.getItem('pendingCreatorSubscription');
    if (pendingSubscription && creator) {
      const { creatorSlug } = JSON.parse(pendingSubscription);
      if (creatorSlug === slug && isLoggedIn()) {
        sessionStorage.removeItem('pendingCreatorSubscription');
        performSubscription();
      }
    }
  }, [creator, slug]);

  // Perform the actual subscription
  const performSubscription = async () => {
    const email = getLoggedInEmail();
    if (!email || !creator) return;
    
    try {
      setSubscribing(true);
      await axios.post(`${API_URL}/api/creators/subscribe`, {
        email: email,
        creator_id: creator.id,
        notify_new_videos: true,
        notify_premieres: true,
        notify_posts: true
      });
      
      setIsSubscribed(true);
      setShowSubscribeModal(false);
      setShowSubscribeSuccess(true);
      localStorage.setItem('user_email', email);
      
      setTimeout(() => setShowSubscribeSuccess(false), 4000);
    } catch (err) {
      console.error('Error subscribing:', err);
    } finally {
      setSubscribing(false);
    }
  };

  const fetchCreatorData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/creators/profiles/slug/${slug}`);
      const profileData = response.data.profile;
      const videosData = response.data.videos || [];
      
      setCreator(profileData);
      setPosts(response.data.posts || []);
      
      // Separate featured video from other videos
      if (profileData.featured_video_id) {
        const featured = videosData.find(v => v.id === profileData.featured_video_id);
        if (featured) {
          setFeaturedVideo(featured);
          setVideos(videosData.filter(v => v.id !== profileData.featured_video_id));
        } else {
          setVideos(videosData);
        }
      } else {
        setVideos(videosData);
      }
      
      // Check subscription status if user email is stored
      const storedEmail = localStorage.getItem('user_email');
      if (storedEmail && profileData) {
        checkSubscriptionStatus(storedEmail, profileData.id);
      }
    } catch (err) {
      console.error('Error fetching creator:', err);
      setError('Creator not found');
    } finally {
      setLoading(false);
    }
  };

  const checkSubscriptionStatus = async (email, creatorId) => {
    try {
      const response = await axios.get(`${API_URL}/api/creators/subscription-status`, {
        params: { email, creator_id: creatorId }
      });
      setIsSubscribed(response.data.is_subscribed);
    } catch (err) {
      console.error('Error checking subscription:', err);
    }
  };

  const handleSubscribe = async () => {
    if (isLoggedIn()) {
      const email = getLoggedInEmail();
      if (email) {
        setSubscribeEmail(email);
        await performSubscription();
        return;
      }
    }
    
    sessionStorage.setItem('pendingCreatorSubscription', JSON.stringify({
      creatorSlug: slug,
      creatorId: creator?.id,
      creatorName: creator?.name
    }));
    sessionStorage.setItem('creatorPageReturn', `/c/${slug}`);
    
    window.location.href = getLoginUrl();
  };

  const handleEmailSubscribe = async () => {
    if (!subscribeEmail) return;
    
    try {
      setSubscribing(true);
      await axios.post(`${API_URL}/api/creators/subscribe`, {
        email: subscribeEmail,
        creator_id: creator.id,
        notify_new_videos: true,
        notify_premieres: true,
        notify_posts: true
      });
      
      setIsSubscribed(true);
      setShowSubscribeModal(false);
      setShowSubscribeSuccess(true);
      localStorage.setItem('user_email', subscribeEmail);
      
      setTimeout(() => setShowSubscribeSuccess(false), 4000);
    } catch (err) {
      console.error('Error subscribing:', err);
    } finally {
      setSubscribing(false);
    }
  };

  const handleUnsubscribe = async () => {
    const email = localStorage.getItem('user_email');
    if (!email) return;
    
    try {
      await axios.post(`${API_URL}/api/creators/unsubscribe`, null, {
        params: { email, creator_id: creator.id }
      });
      setIsSubscribed(false);
    } catch (err) {
      console.error('Error unsubscribing:', err);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: creator.name,
          text: creator.description || `Check out ${creator.name} on LTV VOD`,
          url: window.location.href
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          setShowShareModal(true);
        }
      }
    } else {
      setShowShareModal(true);
    }
  };

  // Carousel scroll handlers
  const updateScrollButtons = () => {
    if (videosContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = videosContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scrollVideos = (direction) => {
    if (videosContainerRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      videosContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setTimeout(updateScrollButtons, 300);
    }
  };

  useEffect(() => {
    updateScrollButtons();
  }, [videos]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-purple-500"></div>
      </div>
    );
  }

  if (error || !creator) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white px-4">
        <div className="text-center">
          <h1 className="text-xl sm:text-2xl font-bold mb-4">Channel Not Found</h1>
          <p className="text-gray-400 mb-6 text-sm sm:text-base">The channel you're looking for doesn't exist.</p>
          <Link to="/vod" className="px-5 py-2.5 sm:px-6 sm:py-3 bg-purple-600 rounded-full hover:bg-purple-700 transition text-sm sm:text-base">
            Back to VOD
          </Link>
        </div>
      </div>
    );
  }

  // Get all videos for display (if no featured, show all)
  const displayVideos = videos;

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>{creator.name} | LTV VOD</title>
        <meta name="description" content={creator.description || `Watch ${creator.name} videos on LTV VOD. Subscribe for notifications on new videos, premieres, and updates.`} />
        <meta name="keywords" content={`${creator.name}, LTV VOD, videos, streaming, subscribe, ${creator.name} channel`} />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
        <link rel="canonical" href={`https://ltvproduction.com/c/${creator.slug}`} />
        
        {/* Open Graph */}
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={`${creator.name} | LTV VOD`} />
        <meta property="og:description" content={creator.description || `Watch ${creator.name} on LTV VOD`} />
        <meta property="og:url" content={`https://ltvproduction.com/c/${creator.slug}`} />
        {creator.profile_image_url && <meta property="og:image" content={creator.profile_image_url} />}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${creator.name} | LTV VOD`} />
        <meta name="twitter:description" content={creator.description || `Watch ${creator.name} on LTV VOD`} />
        
        {/* Schema.org structured data for creator/channel */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            "name": creator.name,
            "description": creator.description || "",
            "url": `https://ltvproduction.com/c/${creator.slug}`,
            "mainEntity": {
              "@type": "Person",
              "name": creator.name,
              "description": creator.description || "",
              "url": `https://ltvproduction.com/c/${creator.slug}`,
              "image": creator.profile_image_url || "",
              "sameAs": Array.isArray(creator.social_links) ? creator.social_links.filter(l => l && l.url).map(link => link.url) : []
            }
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-lg border-b border-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/vod" className="flex items-center gap-2">
            <img 
              src="/ltv-vod-logo.png" 
              alt="LTV VOD" 
              className="h-10 sm:h-12 object-contain"
            />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link 
              to="/vod" 
              className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 text-gray-300 hover:text-white transition rounded-lg hover:bg-white/10 text-sm"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Home</span>
            </Link>
            {!isLoggedIn() && (
              <Link 
                to={getLoginUrl()} 
                className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-full transition text-sm font-medium"
              >
                <LogIn className="w-4 h-4" />
                <span>Sign In</span>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Banner Image */}
      {creator.banner_image_url && (
        <div className="relative w-full h-32 sm:h-48 md:h-56 lg:h-64 overflow-hidden">
          <img 
            src={creator.banner_image_url} 
            alt={`${creator.name} banner`}
            className="w-full h-full object-cover"
          />
          {/* Gradient overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
        
        {/* Creator Header */}
        <div className={`flex items-center gap-3 sm:gap-4 mb-6 ${creator.banner_image_url ? '-mt-12 sm:-mt-16 relative z-10' : ''}`}>
          {/* Profile Image */}
          <div className={`rounded-full overflow-hidden bg-gray-800 flex-shrink-0 ${
            creator.banner_image_url 
              ? 'w-20 h-20 sm:w-28 sm:h-28 border-4 border-black' 
              : 'w-12 h-12 sm:w-16 sm:h-16 border-2 border-gray-700'
          }`}>
            {creator.profile_image_url ? (
              <img 
                src={creator.profile_image_url} 
                alt={creator.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xl font-bold text-purple-400">
                {creator.name.charAt(0)}
              </div>
            )}
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-lg sm:text-xl font-bold truncate">{creator.name}</h1>
              {creator.is_verified && (
                <BadgeCheck className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400 flex-shrink-0" />
              )}
            </div>
            <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-400">
              <span>{creator.subscriber_count?.toLocaleString() || 0} subscribers</span>
              <span className="text-gray-600">•</span>
              <span>{creator.video_count || videos.length + (featuredVideo ? 1 : 0)} videos</span>
              <span className="text-gray-600">•</span>
              <button 
                onClick={() => setActiveTab('about')}
                className="text-purple-400 hover:text-purple-300 transition"
              >
                About
              </button>
            </div>
            {/* Social Links */}
            {creator.social_links && creator.social_links.length > 0 && (
              <div className="flex items-center gap-2 mt-2">
                {creator.social_links.slice(0, 4).map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition text-gray-300 hover:text-white"
                    title={link.platform}
                  >
                    <SocialIcon platform={link.platform} size="sm" />
                  </a>
                ))}
                {creator.social_links.length > 4 && (
                  <button 
                    onClick={() => setActiveTab('about')}
                    className="text-xs text-gray-400 hover:text-white transition"
                  >
                    +{creator.social_links.length - 4} more
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {isSubscribed ? (
              <button
                onClick={handleUnsubscribe}
                className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-700/50 rounded-full hover:bg-gray-700 transition text-xs sm:text-sm text-gray-300 border border-gray-600/50"
              >
                <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-400" />
                <span className="hidden sm:inline">Subscribed</span>
              </button>
            ) : (
              <button
                onClick={handleSubscribe}
                disabled={subscribing}
                className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-purple-600 rounded-full hover:bg-purple-700 transition text-xs sm:text-sm font-medium disabled:opacity-50"
              >
                {subscribing ? (
                  <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                ) : (
                  <Bell className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                )}
                <span>Subscribe</span>
              </button>
            )}
            <button
              onClick={handleShare}
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition"
            >
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Featured/Promoted Video Section */}
        {featuredVideo && (
          <div className="mb-8">
            <Link 
              to={`/vod/video/${featuredVideo.slug || featuredVideo.id}`}
              className="flex flex-col md:flex-row gap-4 group"
            >
              {/* Thumbnail */}
              <div className="relative aspect-video md:w-80 lg:w-96 rounded-xl overflow-hidden bg-gray-800 flex-shrink-0">
                {featuredVideo.thumbnail_url ? (
                  <img 
                    src={featuredVideo.thumbnail_url} 
                    alt={featuredVideo.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Play className="w-12 h-12 text-gray-600" />
                  </div>
                )}
                {featuredVideo.duration && (
                  <span className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-xs font-medium">
                    {formatDuration(featuredVideo.duration)}
                  </span>
                )}
                {/* Play overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                    <Play className="w-6 h-6 text-black fill-black ml-1" />
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-bold mb-1 group-hover:text-purple-400 transition line-clamp-2">
                  {featuredVideo.title}
                </h2>
                <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                  <span>{creator.name}</span>
                  <span className="text-gray-600">•</span>
                  <span>{formatViews(featuredVideo.views || featuredVideo.view_count)}</span>
                  <span className="text-gray-600">•</span>
                  <span>{formatTimeAgo(featuredVideo.created_at)}</span>
                </div>
                {featuredVideo.description && (
                  <p className="text-sm text-gray-400 line-clamp-2 sm:line-clamp-3 leading-relaxed">
                    {featuredVideo.description}
                  </p>
                )}
              </div>
            </Link>
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-gray-800 mb-6"></div>

        {/* For You / All Videos Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg sm:text-xl font-bold">For You</h3>
            {displayVideos.length > 4 && (
              <div className="hidden sm:flex gap-2">
                <button
                  onClick={() => scrollVideos('left')}
                  disabled={!canScrollLeft}
                  className={`w-9 h-9 rounded-full border border-gray-700 flex items-center justify-center transition ${
                    canScrollLeft ? 'hover:bg-gray-800 text-white' : 'text-gray-600 cursor-not-allowed'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => scrollVideos('right')}
                  disabled={!canScrollRight}
                  className={`w-9 h-9 rounded-full border border-gray-700 flex items-center justify-center transition ${
                    canScrollRight ? 'hover:bg-gray-800 text-white' : 'text-gray-600 cursor-not-allowed'
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {displayVideos.length === 0 ? (
            <div className="text-center py-12 bg-gray-900/30 rounded-xl border border-gray-800">
              <Video className="w-12 h-12 mx-auto mb-3 text-gray-600" />
              <p className="text-gray-400 mb-2">No videos yet</p>
              <p className="text-gray-500 text-sm">Subscribe to get notified when {creator.name} uploads!</p>
            </div>
          ) : (
            <div 
              ref={videosContainerRef}
              onScroll={updateScrollButtons}
              className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4 sm:mx-0 sm:px-0"
              style={{ scrollSnapType: 'x mandatory' }}
            >
              {displayVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/vod/video/${video.slug || video.id}`}
                  className="flex-shrink-0 w-[280px] sm:w-[300px] group"
                  style={{ scrollSnapAlign: 'start' }}
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-video rounded-xl overflow-hidden bg-gray-800 mb-2">
                    {video.thumbnail_url ? (
                      <img 
                        src={video.thumbnail_url} 
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Play className="w-10 h-10 text-gray-600" />
                      </div>
                    )}
                    {video.duration && (
                      <span className="absolute bottom-2 right-2 bg-black/80 px-1.5 py-0.5 rounded text-xs">
                        {formatDuration(video.duration)}
                      </span>
                    )}
                    {video.is_premium_exclusive && (
                      <span className="absolute top-2 left-2 bg-gradient-to-r from-purple-600 to-pink-600 px-2 py-0.5 rounded text-xs font-semibold">
                        PREMIUM
                      </span>
                    )}
                    {/* 3-dot menu placeholder */}
                    <button 
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                      onClick={(e) => e.preventDefault()}
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Info */}
                  <h4 className="font-medium text-sm line-clamp-2 mb-1 group-hover:text-purple-400 transition">
                    {video.title}
                  </h4>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <span>{formatViews(video.views || video.view_count)}</span>
                    <span className="text-gray-600">•</span>
                    <span>{formatTimeAgo(video.created_at)}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Tabs for additional content */}
        {posts.length > 0 && (
          <>
            <div className="flex gap-4 border-b border-gray-800 mb-6">
              <button
                onClick={() => setActiveTab('videos')}
                className={`pb-3 px-1 font-medium transition text-sm ${
                  activeTab === 'videos' 
                    ? 'text-white border-b-2 border-purple-500' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Videos
              </button>
              <button
                onClick={() => setActiveTab('posts')}
                className={`pb-3 px-1 font-medium transition text-sm ${
                  activeTab === 'posts' 
                    ? 'text-white border-b-2 border-purple-500' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Posts
              </button>
              <button
                onClick={() => setActiveTab('about')}
                className={`pb-3 px-1 font-medium transition text-sm ${
                  activeTab === 'about' 
                    ? 'text-white border-b-2 border-purple-500' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                About
              </button>
            </div>

            {/* Posts Tab */}
            {activeTab === 'posts' && (
              <div className="max-w-2xl mx-auto space-y-4">
                {posts.map((post) => (
                  <div key={post.id} className="bg-gray-900/50 rounded-xl p-4 border border-gray-800">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-full overflow-hidden bg-gray-700 flex-shrink-0">
                        {creator.profile_image_url ? (
                          <img src={creator.profile_image_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-sm font-bold text-purple-400">
                            {creator.name.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{creator.name}</p>
                        <p className="text-xs text-gray-500">{formatTimeAgo(post.created_at)}</p>
                      </div>
                    </div>
                    <p className="text-gray-200 whitespace-pre-wrap text-sm leading-relaxed">{post.content}</p>
                    {post.image_urls && post.image_urls.length > 0 && (
                      <div className={`grid gap-2 mt-3 ${post.image_urls.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                        {post.image_urls.map((url, index) => (
                          <img key={index} src={url} alt="" className="rounded-lg w-full object-cover max-h-64" />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* About Tab */}
            {activeTab === 'about' && (
              <div className="max-w-2xl mx-auto">
                <h2 className="text-lg font-bold mb-3">About {creator.name}</h2>
                <p className="text-gray-300 leading-relaxed mb-5 text-sm">
                  {creator.description || `Welcome to ${creator.name}'s channel on LTV VOD.`}
                </p>
                
                {creator.social_links && creator.social_links.length > 0 && (
                  <div className="mb-5">
                    <h3 className="font-semibold mb-3 text-sm">Links</h3>
                    <div className="flex flex-wrap gap-3">
                      {creator.social_links.map((link, index) => (
                        <a
                          key={index}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition text-sm"
                        >
                          <SocialIcon platform={link.platform} />
                          <span className="capitalize">{link.platform}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div className="p-4 bg-gray-900/50 rounded-xl border border-gray-800">
                  <h3 className="font-semibold mb-2 text-sm">Channel Stats</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-400 text-xs">Subscribers</p>
                      <p className="text-lg font-bold">{creator.subscriber_count?.toLocaleString() || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Videos</p>
                      <p className="text-lg font-bold">{creator.video_count || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Subscribe Modal */}
      {showSubscribeModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 p-0 sm:p-4">
          <div className="bg-gray-900 rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 w-full sm:max-w-md border-t sm:border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Subscribe to {creator.name}</h2>
              <button onClick={() => setShowSubscribeModal(false)} className="p-1 text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-400 mb-4 text-sm">Get notified when new videos are uploaded!</p>
            <input
              type="email"
              value={subscribeEmail}
              onChange={(e) => setSubscribeEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full px-4 py-3 bg-black border border-gray-700 rounded-xl focus:border-purple-500 focus:outline-none mb-4 text-white text-sm"
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowSubscribeModal(false)}
                className="flex-1 px-4 py-2.5 bg-gray-800 rounded-xl hover:bg-gray-700 transition text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleEmailSubscribe}
                disabled={!subscribeEmail || subscribing}
                className="flex-1 px-4 py-2.5 bg-purple-600 rounded-xl hover:bg-purple-700 transition disabled:opacity-50 font-medium text-sm"
              >
                {subscribing ? 'Subscribing...' : 'Subscribe'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscribe Success Toast */}
      {showSubscribeSuccess && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-bottom duration-300">
          <div className="bg-green-600 text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-3">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Subscribed to {creator?.name}!</span>
          </div>
        </div>
      )}

      {/* Social Share Modal */}
      {showShareModal && creator && (
        <SocialShareModal 
          event={{
            title: creator.name,
            slug: creator.slug,
            thumbnail_url: creator.profile_image_url,
            social_share_image: creator.social_media_image_url || creator.banner_image_url || creator.profile_image_url
          }}
          shareUrl={`https://ltvproduction.com/c/${creator.slug}`}
          shareType="creator"
          onClose={() => setShowShareModal(false)}
        />
      )}
      
      {/* Footer */}
      <VODFooter />
    </div>
  );
};

export default CreatorChannelPage;
